import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Modal, Result, Spin } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import MasterModal from 'components/organisms/masterModal';
import { useMembersForPod } from 'utils/hooks/pods/useMembersForPod';

import AssignedMembersSection from './assigned-members-section';
import AssignStateClinicSection from './assign-state-clinic-section';
import BasicInfoSection from './basic-info-section';
import PodSummaryPanel from '../pod-summary-panel';
import { generatePodName } from '../../utils/generatePodName';
import { useCreatePod } from '../../hooks/use-create-pod';
import { useUpdatePod } from '../../hooks/use-update-pod';
import { useGetPod } from '../../hooks/use-pod-management';
import { createPodValidationSchema } from './validation-schema';
import s from './styles.module.scss';

import type { ICreatePodFormValues } from './validation-schema';
import type { FC } from 'react';

interface ImpactfulChangeSummary {
  removedClinicsWithPatients: {
    count: number;
    totalPatients: number;
  };
  removedMembers: {
    count: number;
  };
  hasImpact: boolean;
}

const detectImpactfulChanges = (
  original: ICreatePodFormValues,
  current: ICreatePodFormValues,
): ImpactfulChangeSummary => {
  const currentClinicGuids = new Set(current.clinicGuids.map((c) => c.guid));

  const removedClinics = original.clinicGuids.filter(
    (c) => !currentClinicGuids.has(c.guid),
  );
  const removedClinicsWithPatients = removedClinics.filter(
    (c) => c.patientCount > 0,
  );
  const totalRemovedPatients = removedClinicsWithPatients.reduce(
    (sum, c) => sum + c.patientCount,
    0,
  );

  const currentMemberGuids = new Set(current.members.map((m) => m.loginGuid));
  const removedMembers = original.members.filter(
    (m) => !currentMemberGuids.has(m.loginGuid),
  );

  const hasImpact =
    removedClinicsWithPatients.length > 0 || removedMembers.length > 0;

  return {
    removedClinicsWithPatients: {
      count: removedClinicsWithPatients.length,
      totalPatients: totalRemovedPatients,
    },
    removedMembers: {
      count: removedMembers.length,
    },
    hasImpact,
  };
};

export const PodFormModeContext = createContext<{ isEditMode: boolean }>({
  isEditMode: false,
});
export const usePodFormMode = () => useContext(PodFormModeContext);

interface ICreatePodModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new pod GUID after a successful create */
  onCreated?: (podGuid: string) => void;
  /** When set, modal opens in Edit mode with this pod guid pre-populated */
  editPodGuid?: string | null;
}

const createInitialValues: ICreatePodFormValues = {
  podName: 'RPM - New Pod',
  isActive: true,
  podType: 'RPM',
  states: [],
  clinicGuids: [],
  members: [],
};

const CreatePodModal: FC<ICreatePodModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  editPodGuid,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!editPodGuid;

  const {
    pod: editPodData,
    clinics: editClinics,
    members: editMembers,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useGetPod(editPodGuid ?? '', { enabled: isEditMode && isOpen });

  // useMembersForPod provides role data that the pod detail API does not include.
  // Stored in a ref to avoid triggering enableReinitialize when the member list
  // loads asynchronously — that would silently discard in-progress user edits.
  const { members: allAgencyMembers } = useMembersForPod();
  const allAgencyMembersRef = useRef(allAgencyMembers);
  allAgencyMembersRef.current = allAgencyMembers;

  // Freeze editInitialValues in a ref so background refetches (e.g. window
  // focus) don't reset the form via enableReinitialize while the user is editing.
  const editInitialValuesRef = useRef<ICreatePodFormValues | undefined>(
    undefined,
  );

  const editInitialValues = useMemo(() => {
    if (!isEditMode || !editPodData || !editClinics || !editMembers)
      return undefined;

    // Build a lookup map for role enrichment (uses ref to avoid dep-array race)
    const memberRoleMap = new Map(
      allAgencyMembersRef.current.map((m) => [m.loginGuid, m.role]),
    );

    return {
      podName: editPodData.podName,
      isActive: editPodData.status === 'active',
      podType: editPodData.podType,
      states: editPodData.states ?? [],
      clinicGuids: editClinics.map((c) => ({
        guid: c.guid,
        name: c.name,
        state: c.state ?? '',
        patientCount: c.patientCount,
      })),
      members: editMembers.map((m) => ({
        loginGuid: m.loginGuid,
        name: m.name,
        role: m.role ?? memberRoleMap.get(m.loginGuid) ?? '',
        priority: m.priority as 'primary' | 'secondary' | 'tertiary',
      })),
    };
  }, [isEditMode, editPodData, editClinics, editMembers]);

  // Capture the first non-undefined value only; subsequent refetches are ignored.
  if (editInitialValues && !editInitialValuesRef.current) {
    editInitialValuesRef.current = editInitialValues;
  }

  const frozenEditValues = editInitialValuesRef.current;

  const [inactivePodError, setInactivePodError] = useState<string | null>(null);
  const valuesAtErrorRef = useRef<ICreatePodFormValues | null>(null);

  const formik = useFormik<ICreatePodFormValues>({
    initialValues: frozenEditValues ?? createInitialValues,
    enableReinitialize: true,
    validationSchema: createPodValidationSchema(),
    validateOnChange: false,
    onSubmit: (values, { setFieldValue }) => {
      const trimmedName = values.podName.trimEnd();
      if (trimmedName !== values.podName) {
        setFieldValue('podName', trimmedName);
      }
      const trimmedValues = { ...values, podName: trimmedName };

      if (isEditMode) {
        const original = editInitialValuesRef.current;
        if (!original || !editPodGuid) return;

        setInactivePodError(null);

        const impact = detectImpactfulChanges(original, trimmedValues);
        if (impact.hasImpact) {
          showImpactWarningConfirm(impact, trimmedValues);
          return;
        }

        updatePod(editPodGuid, original, trimmedValues);
        return;
      }
      createPod(trimmedValues);
    },
  });

  const summaryValues = useMemo(() => {
    const { clinicGuids } = formik.values;
    const clinicsSelected = clinicGuids.length;
    const patients = clinicGuids.reduce(
      (sum, clinic) => sum + (clinic.patientCount || 0),
      0,
    );
    return { clinicsSelected, patients };
  }, [formik.values.clinicGuids]);

  const lastAutoGeneratedNameRef = useRef<string>('RPM - New Pod');

  // Initialize auto-gen ref for Edit mode
  useEffect(() => {
    if (isEditMode && editPodData && editClinics) {
      lastAutoGeneratedNameRef.current = generatePodName({
        podType: editPodData.podType,
        states: editPodData.states ?? [],
        clinicNames: editClinics.map((c) => c.name),
      });
    }
  }, [isEditMode, editPodData, editClinics]);

  // Auto-generate pod name when podType, states, or clinicGuids change.
  // Dep array intentionally excludes formik.values.podName (would cause infinite loop)
  // and formik (new object each render).
  useEffect(() => {
    if (formik.values.podName !== lastAutoGeneratedNameRef.current) {
      return;
    }

    const newName = generatePodName({
      podType: formik.values.podType,
      states: formik.values.states,
      clinicNames: formik.values.clinicGuids.map((c) => c.name),
    });

    lastAutoGeneratedNameRef.current = newName;
    formik.setFieldValue('podName', newName);
  }, [
    formik.values.podType,
    formik.values.states,
    formik.values.clinicGuids,
    formik.setFieldValue,
  ]);

  const isConfirmShowingRef = useRef(false);
  const isImpactConfirmShowingRef = useRef(false);

  const closeAndReset = () => {
    formik.resetForm();
    setInactivePodError(null);
    valuesAtErrorRef.current = null;
    editInitialValuesRef.current = undefined;
    if (!isEditMode) {
      lastAutoGeneratedNameRef.current = 'RPM - New Pod';
    }
    onClose();
  };

  const handleClose = () => {
    if (isSubmitting) return;

    if (!formik.dirty) {
      closeAndReset();
      return;
    }

    if (isConfirmShowingRef.current) return;
    isConfirmShowingRef.current = true;

    Modal.confirm({
      title: t('labels.discardChanges'),
      content: t('labels.unsavedChangesWarning'),
      okText: t('labels.discard'),
      cancelText: t('labels.keepEditing'),
      autoFocusButton: 'cancel',
      centered: true,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        closeAndReset();
      },
      afterClose: () => {
        isConfirmShowingRef.current = false;
      },
    });
  };

  const { createPod, isLoading: isCreateLoading } = useCreatePod({
    onSuccess: (podGuid) => {
      onCreated?.(podGuid);
      closeAndReset();
    },
    onDuplicateName: (message) => {
      formik.setFieldError('podName', message);
    },
  });

  const { updatePod, isLoading: isUpdating } = useUpdatePod({
    onSuccess: () => {
      setInactivePodError(null);
      valuesAtErrorRef.current = null;
      closeAndReset();
    },
    onDuplicateName: (msg) => {
      formik.setFieldError('podName', msg);
    },
    onInactivePod: (msg) => {
      setInactivePodError(msg);
      valuesAtErrorRef.current = formik.values;
    },
  });

  const isSubmitting = isCreateLoading || isUpdating;

  const showImpactWarningConfirm = (
    impact: ImpactfulChangeSummary,
    values: ICreatePodFormValues,
  ) => {
    if (isImpactConfirmShowingRef.current) return;
    isImpactConfirmShowingRef.current = true;

    const lines: string[] = [];
    if (impact.removedClinicsWithPatients.count > 0) {
      lines.push(
        t('labels.removingClinicsWarning', {
          count: impact.removedClinicsWithPatients.count,
          patients: impact.removedClinicsWithPatients.totalPatients,
        }),
      );
    }
    if (impact.removedMembers.count > 0) {
      lines.push(
        t('labels.removingMembersWarning', {
          count: impact.removedMembers.count,
        }),
      );
    }

    Modal.confirm({
      title: t('labels.impactfulChangeWarning'),
      content: lines.map((line, i) => <div key={i}>{line}</div>),
      okText: t('labels.confirmChanges'),
      cancelText: t('labels.keepEditing'),
      autoFocusButton: 'cancel',
      centered: true,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        const original = editInitialValuesRef.current;
        if (!editPodGuid || !original) return;
        updatePod(editPodGuid, original, values);
      },
      afterClose: () => {
        isImpactConfirmShowingRef.current = false;
      },
    });
  };

  useEffect(() => {
    if (inactivePodError && valuesAtErrorRef.current) {
      const valuesChanged = !isEqual(formik.values, valuesAtErrorRef.current);
      if (valuesChanged) {
        setInactivePodError(null);
        valuesAtErrorRef.current = null;
      }
    }
  }, [formik.values, inactivePodError]);

  useEffect(() => {
    if (formik.submitCount > 0) {
      formik.validateForm();
    }
  }, [formik.values, formik.submitCount]);

  const modalTitle = isEditMode ? t('labels.editPod') : t('labels.createPod');

  const submitLabel = isEditMode
    ? t('labels.saveChanges')
    : t('labels.createPod');

  const renderModalBody = () => {
    if (isEditMode && isEditLoading) {
      return (
        <div className={s.loadingContainer}>
          <Spin size="large" />
        </div>
      );
    }

    if (isEditMode && isEditError) {
      return (
        <div className={s.loadingContainer}>
          <Result
            status="error"
            title={t('labels.unableToLoadPodData')}
            extra={<Button onClick={closeAndReset}>{t('labels.close')}</Button>}
          />
        </div>
      );
    }

    return (
      <>
        <div className={s.twoColumnLayout}>
          <div className={s.formColumn}>
            <PodFormModeContext.Provider value={{ isEditMode }}>
              <FormikProvider value={formik}>
                <BasicInfoSection />
                <div className={s.sectionDivider} />
                <AssignStateClinicSection />
                <div className={s.sectionDivider} />
                <AssignedMembersSection />
              </FormikProvider>
            </PodFormModeContext.Provider>
          </div>
          <div className={s.summaryColumn}>
            <PodSummaryPanel
              clinicsSelected={summaryValues.clinicsSelected}
              patients={summaryValues.patients}
              maAssigned={0}
              maSuggested={0}
              rnAssigned={0}
              rnSuggested={0}
            />
          </div>
        </div>
        {inactivePodError && (
          <Alert
            type="error"
            showIcon
            message={inactivePodError}
            className={s.inactivePodError}
            data-cy="edit-pod__inactive-error"
          />
        )}
      </>
    );
  };

  return (
    <MasterModal
      isOpen={isOpen}
      toggle={handleClose}
      title={modalTitle}
      size="huge"
      centered
      zIndex={1050}
      bodyClassName={s.modalBody}
      footer={
        <>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            data-cy={
              isEditMode ? 'edit-pod__cancel-btn' : 'create-pod__cancel-btn'
            }
          >
            {t('labels.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={isSubmitting || (isEditMode && !formik.dirty)}
            onClick={() => formik.handleSubmit()}
            data-cy={
              isEditMode ? 'edit-pod__submit-btn' : 'create-pod__submit-btn'
            }
          >
            {submitLabel}
          </Button>
        </>
      }
      dataCy={isEditMode ? 'edit-pod-modal' : 'create-pod-modal'}
    >
      {renderModalBody()}
    </MasterModal>
  );
};

export default CreatePodModal;
